import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchStandardCourse, fetchStandardCourses, createClassFromStandardCourse } from "../../../api/backend";
import StandardCoursePreview from "./StandardCoursePreview";
import KbCatalog, { CategoryPill, RowFigure } from "../../../components/knowledgeBank/catalog/KbCatalog";

const CATEGORY_LABEL = { AHA: "AHA" };

const lengthOf = (c) => ((c.totalHours || 0) <= 8 ? "Up to 8 hrs" : c.totalHours <= 40 ? "9–40 hrs" : "Over 40 hrs");
const FACETS = [
  { key: "cat", title: "Category", fn: (c) => CATEGORY_LABEL[c.category] || c.category },
  { key: "len", title: "Length", fn: lengthOf, order: ["Up to 8 hrs", "9–40 hrs", "Over 40 hrs"] },
];

/** Counts as short text: "3 sessions · 2 skill sheets · 1 test · 4 documents". */
export function contentSummary(c) {
  const part = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  return [
    part(c.sessionCount ?? 0, "session", "sessions"),
    part(c.skillSheetCount ?? 0, "skill sheet", "skill sheets"),
    part(c.testCount ?? 0, "test", "tests"),
    part(c.documentCount ?? 0, "document", "documents"),
  ].join(" · ");
}

function messageOf(error, fallback) {
  return error?.response?.data?.message || fallback;
}

/**
 * The Standard section of the Knowledge Bank: courses taught identically at every training center. A coordinator can
 * look at one read-only and use it to create a class. The content cannot be changed there: only the course's
 * global admins change it.
 */
export default function StandardCoursesTab({ tenantId }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [using, setUsing] = useState(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [useError, setUseError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return fetchStandardCourses(tenantId)
      .then((data) => { setCourses(Array.isArray(data) ? data : []); setError(null); })
      .catch((e) => setError(messageOf(e, "The standard courses could not be loaded.")))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (course) => {
    setPreviewLoading(true);
    setPreview({ summary: course, detail: null });
    try {
      const detail = await fetchStandardCourse(tenantId, course.id);
      setPreview({ summary: course, detail });
    } catch (e) {
      setPreview(null);
      setError(messageOf(e, "This course could not be opened."));
    } finally {
      setPreviewLoading(false);
    }
  };

  const startUse = (course) => { setUsing(course); setLabel(""); setUseError(null); };

  const confirmUse = async () => {
    setCreating(true);
    setUseError(null);
    try {
      const created = await createClassFromStandardCourse(tenantId, using.id, label.trim());
      setUsing(null);
      navigate(`/course-management/${created.id}`);
    } catch (e) {
      setUseError(messageOf(e, "The class could not be created."));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box data-testid="standard-courses-tab">
      <Typography sx={{ fontSize: 14.5, color: "var(--color-accent-violet-mid)", mb: 2.5, maxWidth: "72ch" }}>
        Courses taught the same way at every training center. Their content is set by Training Center and cannot be changed:
        choose one to create a class, then schedule it with your dates, rooms and instructors.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {courses.length === 0 && !error ? (
        <Box data-testid="standard-courses-empty" sx={{ p: 4, textAlign: "center", border: "1px dashed var(--color-hairline-cloud)", borderRadius: "12px" }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No standard courses are available to your training center yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Standard courses show here once they are published for a category your training center offers, such as AHA under
            Training Center Settings, Certifications offered.
          </Typography>
        </Box>
      ) : (
        <KbCatalog items={courses} noun="standard course" searchPlaceholder="Search by name, course number or category"
          facets={FACETS} searchText={(c) => `${c.title} ${c.courseNumber || ""} ${c.category || ""} ${c.description || ""}`}
          sorts={[
            ["name", "Name", (a, b) => a.title.localeCompare(b.title)],
            ["cat", "Category", (a, b) => (a.category || "").localeCompare(b.category || "") || a.title.localeCompare(b.title)],
            ["hours", "Shortest first", (a, b) => (a.totalHours || 0) - (b.totalHours || 0)],
          ]}
          renderRow={(c) => ({
            testId: `standard-course-${c.id}`,
            top: <>
              <Chip size="small" label="Standard" color="primary" variant="outlined" />
              <CategoryPill label={CATEGORY_LABEL[c.category] || c.category} />
              {c.courseNumber && <Typography component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{c.courseNumber}</Typography>}
              <Typography component="span" sx={{ fontSize: 12, color: "var(--color-accent-violet-mid)" }}>Version {c.versionNumber}</Typography>
            </>,
            title: c.title,
            description: c.description,
            meta: <span>{contentSummary(c)}</span>,
            figure: c.totalHours ? <RowFigure value={c.totalHours} unit={c.totalHours === 1 ? "hr" : "hrs"} /> : null,
            actions: <>
              <Button size="small" variant="outlined" onClick={() => openPreview(c)}>Preview</Button>
              <Button size="small" variant="contained" onClick={() => startUse(c)}>Use this course</Button>
            </>,
          })} />
      )}

      <StandardCoursePreview
        open={Boolean(preview)}
        loading={previewLoading}
        summary={preview?.summary}
        detail={preview?.detail}
        onClose={() => setPreview(null)}
        onUse={() => { const c = preview.summary; setPreview(null); startUse(c); }}
      />

      <Dialog open={Boolean(using)} onClose={() => !creating && setUsing(null)} fullWidth maxWidth="sm" aria-labelledby="use-standard-title">
        <DialogTitle id="use-standard-title">Use {using?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This creates a new class with the course exactly as published (version {using?.versionNumber}). You can then set its
            dates, rooms, instructors and seats. The course content cannot be changed once the class is created.
          </Typography>
          <TextField fullWidth size="small" label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: 100 }} helperText="For example a cohort or term. The title and course number stay the same." disabled={creating} />
          {useError && <Alert severity="error" sx={{ mt: 2 }}>{useError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsing(null)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={confirmUse} disabled={creating}>{creating ? "Creating…" : "Create class"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
